import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

/**
 * Admin login page with specialized branding for HR Admin portal.
 * Does not include demo mode option - admins must authenticate via MSAL/OIDC.
 */
const AdminLoginPage: React.FC = () => {
  const { 
    signIn, 
    isLoading, 
    authError
  } = useAuth();

  const handleMicrosoftSignIn = async (): Promise<void> => {
    try {
      // Store in localStorage BEFORE any async operations
      const returnPath = window.location.pathname;
      localStorage.setItem('adminReturnUrl', returnPath);
      
      // Verify it was saved
      const saved = localStorage.getItem('adminReturnUrl');
      console.log('AdminLoginPage: Microsoft sign-in initiated', { 
        returnPath, 
        saved,
        localStorageWorks: saved === returnPath 
      });
      
      await signIn('msal', returnPath);
    } catch (error) {
      console.error('Microsoft login failed:', error);
      localStorage.removeItem('adminReturnUrl'); // Clean up on error
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rm-primary-50 via-rm-bg-0 to-rm-primary-100 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-rm-primary-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-rm-primary-300 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <Card className="shadow-2xl border-0 overflow-hidden">
          {/* Decorative top border - darker for admin */}
          <div className="h-1.5 bg-gradient-to-r from-rm-primary-600 via-rm-primary-700 to-rm-primary-800"></div>
          
          <CardHeader className="text-center pt-12 pb-6 px-8">
            <div className="mb-6 inline-flex items-center justify-center mx-auto">
              <img 
                src="/logo192.png" 
                alt="Renewmyday" 
                className="w-24 h-24 rounded-full shadow-lg ring-4 ring-rm-primary-200"
              />
            </div>
            <CardTitle className="text-3xl font-bold text-rm-primary-700 leading-tight">
              HR Admin
            </CardTitle>
            <p className="mt-2 text-lg text-rm-primary-600 font-medium">
              Admin & HR Analytics
            </p>
          </CardHeader>
          <CardContent className="space-y-8 px-8 pb-12">
            <p className="text-center text-base text-rm-neutral-600 leading-relaxed">
              Sign in to access reports, session analytics, and evaluation tools for your organization.
            </p>
            
            {authError && (
              <div className="p-4 text-red-700 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">{authError}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {/* Alternative Sign-in (Microsoft) - Always visible for admin */}
              {/* Alternative Sign-in (Microsoft) - Always visible for admin */}
              <button
                onClick={handleMicrosoftSignIn}
                disabled={isLoading}
                className="w-full px-4 py-2.5 bg-white text-rm-neutral-700 hover:text-rm-primary-700 hover:bg-rm-bg-50 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rm-primary-300 disabled:opacity-50 border border-rm-neutral-300"
              >
                <span className="text-sm font-medium">{isLoading ? 'Signing in...' : 'Alternative Sign-in'}</span>
              </button>
            </div>

            {/* Info note about admin access */}
            <div className="text-center text-sm text-rm-neutral-500">
              <p>Access requires <strong>Reports Reader</strong> role or higher.</p>
              <p className="mt-1">Contact your administrator if you need access.</p>
            </div>
          </CardContent>
        </Card>

        {/* Link to regular app */}
        <div className="text-center">
          <a 
            href="/" 
            className="text-sm text-rm-primary-600 hover:text-rm-primary-700 font-medium transition-colors"
          >
            ← Go to Wellbeing App
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
